import React, { useState, useRef, useEffect } from "react";

/**
 * ResizableDiv - Ultimate Flexible Component
 *
 * Features:
 * - Width OR Height resizing
 * - Percentage OR Pixel mode
 * - Min/Max limits
 * - localStorage support
 * - Callback on resize
 *
 * @param {ReactNode} children - Content to render
 * @param {string} direction - 'horizontal' (width) or 'vertical' (height)
 * @param {number} initialSize - Initial size (% or px)
 * @param {number} minSize - Minimum size (% or px)
 * @param {number} maxSize - Maximum size (% or px)
 * @param {boolean} usePercentage - Use percentage (true) or pixels (false)
 * @param {string} storageKey - localStorage key to save size
 * @param {function} onResize - Callback function(newSize)
 */
const ResizableDiv = ({
  children,
  direction = "horizontal", // 'horizontal' (width) or 'vertical' (height)
  initialSize = 25, // Default 25% or 25px
  minSize = 15, // Minimum
  maxSize = 40, // Maximum
  usePercentage = true, // true = %, false = px
  storageKey = null, // localStorage key
  onResize = null, // Callback
}) => {
  // Load from localStorage if key provided
  const [size, setSize] = useState(() => {
    if (storageKey) {
      const saved = localStorage.getItem(storageKey);
      return saved ? parseFloat(saved) : initialSize;
    }
    return initialSize;
  });

  const [isResizing, setIsResizing] = useState(false);
  const startPos = useRef(0);
  const startSize = useRef(0);

  const isHorizontal = direction === "horizontal";

  const handleMouseDown = (e) => {
    setIsResizing(true);
    startPos.current = isHorizontal ? e.clientX : e.clientY;
    startSize.current = size;
    document.body.style.cursor = isHorizontal ? "col-resize" : "row-resize";
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;

    if (usePercentage) {
      // Calculate as percentage
      const totalSize = isHorizontal ? window.innerWidth : window.innerHeight;
      const currentPos = isHorizontal ? e.clientX : e.clientY;
      const newSizePercent = (currentPos / totalSize) * 100;
      const clampedSize = Math.max(minSize, Math.min(maxSize, newSizePercent));

      setSize(clampedSize);

      // Save to localStorage
      if (storageKey) {
        localStorage.setItem(storageKey, clampedSize.toString());
      }

      // Callback
      if (onResize) {
        onResize(clampedSize);
      }
    } else {
      // Calculate as pixels
      const currentPos = isHorizontal ? e.clientX : e.clientY;
      const diff = currentPos - startPos.current;
      const newSize = startSize.current + diff;
      const clampedSize = Math.max(minSize, Math.min(maxSize, newSize));

      setSize(clampedSize);

      // Save to localStorage
      if (storageKey) {
        localStorage.setItem(storageKey, clampedSize.toString());
      }

      // Callback
      if (onResize) {
        onResize(clampedSize);
      }
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
    document.body.style.cursor = "default";
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizing]);

  const sizeValue = usePercentage ? `${size}%` : `${size}px`;

  // Handle styles based on direction
  const containerStyle = {
    position: "relative",
    display: isHorizontal ? "inline-block" : "block",
    ...(isHorizontal
      ? { width: sizeValue, height: "100%" }
      : { height: sizeValue, width: "100%" }),
  };

  // Resize handle styles
  const handleStyle = {
    position: "absolute",
    cursor: isHorizontal ? "col-resize" : "row-resize",
    backgroundColor: isResizing ? "rgba(0, 123, 255, 0.2)" : "transparent",
    transition: isResizing ? "none" : "all 0.2s",
    zIndex: 999,
    ...(isHorizontal
      ? {
          right: "-2px",
          top: 0,
          width: "0px",
          height: "100%",
          borderLeft: isResizing ? "1px solid #007bff" : "1px solid #ddd",
          borderRight: isResizing ? "1px solid #007bff" : "1px solid #ddd",
        }
      : {
          bottom: "-2px",
          left: 0,
          height: "0px",
          width: "100%",
          borderTop: isResizing ? "1px solid #007bff" : "1px solid #ddd",
          borderBottom: isResizing ? "1px solid #007bff" : "1px solid #ddd",
        }),
  };

  // Visual indicator styles
  const indicatorStyle = {
    position: "absolute",
    backgroundColor: isResizing ? "#007bff" : "#aaa",
    borderRadius: "3px",
    opacity: 0.7,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontSize: "8px",
    pointerEvents: "none",
    ...(isHorizontal
      ? {
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: "8px",
          height: "25px",
        }
      : {
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: "25px",
          height: "8px",
        }),
  };

  return (
    <div style={containerStyle}>
      {/* Content */}
      {children}

      {/* Resize handle */}
      <div onMouseDown={handleMouseDown} style={handleStyle}>
        {/* Visual indicator */}
        <div style={indicatorStyle}>{isHorizontal ? "⋮" : "⋯"}</div>
      </div>
    </div>
  );
};

export default ResizableDiv;
